import { memo } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, RefreshCw } from 'lucide-react';
import PropertyCard from '../property/PropertyCard';
import { useFeaturedProperties } from '../../hooks/useFeaturedProperties';
import { convertPropertySummaryToDisplay } from '../../lib/api/properties';
import Skeleton from '../ui/Skeleton';

const FeaturedPropertiesSection = memo(() => {
  const { featuredProperties, isLoading: featuredLoading, error: featuredError } = useFeaturedProperties();

  // Loading state with skeleton loaders to prevent flickering
  if (featuredLoading) {
    return (
      <section
        className="bg-neutral-50 py-20 sm:py-24"
        aria-labelledby="featured-properties-heading"
        role="region"
        aria-busy="true"
      >
        <div className="container mx-auto w-full max-w-screen-2xl px-4 sm:px-6 lg:px-8">
          <header className="mx-auto mb-14 max-w-3xl text-center sm:mb-16">
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-primary-600">
              Featured
            </p>
            <h2
              id="featured-properties-heading"
              className="text-balance text-3xl font-semibold tracking-tight text-neutral-900 sm:text-4xl md:text-[2.25rem] md:leading-snug"
            >
              Handpicked homes you can move into soon
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-neutral-600">
              Curated properties our team would confidently recommend to friends and family.
            </p>
          </header>

          {/* Skeleton loaders that match the actual property card layout */}
          <div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            role="list"
            aria-label="Loading featured properties"
          >
            {Array.from({ length: 3 }).map((_, index) => (
              <div key={index} role="listitem">
                <Skeleton.PropertyCard />
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  // Error state with retry functionality
  if (featuredError) {
    return (
      <section
        className="bg-neutral-50 py-20 sm:py-24"
        aria-labelledby="featured-properties-heading"
        role="region"
      >
        <div className="container mx-auto w-full max-w-screen-2xl px-4 sm:px-6 lg:px-8">
          <header className="mx-auto mb-14 max-w-3xl text-center sm:mb-16">
            <h2
              id="featured-properties-heading"
              className="text-3xl md:text-4xl font-semibold text-neutral-900 mb-4"
            >
              Featured Properties
            </h2>
            <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
              Discover our hand-picked selection of premium properties
            </p>
          </header>
          
          <div 
            className="bg-red-50 border border-red-200 rounded-xl p-6 max-w-md mx-auto text-center"
            role="alert"
            aria-live="assertive"
          >
            <AlertCircle className="h-8 w-8 text-red-600 mx-auto mb-4" aria-hidden="true" />
            <h3 className="text-lg font-semibold text-red-800 mb-2">
              Unable to Load Featured Properties
            </h3>
            <p className="text-red-700 mb-4">
              Error loading featured properties: {featuredError}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
              aria-describedby="retry-description"
            >
              <RefreshCw className="h-4 w-4 mr-2" aria-hidden="true" />
              Try Again
            </button>
            <div id="retry-description" className="sr-only">
              Reload the page to try loading featured properties again
            </div>
          </div>
        </div>
      </section>
    );
  }


  return (
    <section
      className="bg-neutral-50 py-20 sm:py-24"
      aria-labelledby="featured-properties-heading"
      role="region"
    >
      <div className="container mx-auto w-full max-w-screen-2xl px-4 sm:px-6 lg:px-8">
        <header className="mx-auto mb-14 max-w-3xl text-center sm:mb-16">
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-primary-600">
            Featured
          </p>
          <h2
            id="featured-properties-heading"
            className="text-balance text-3xl font-semibold tracking-tight text-neutral-900 sm:text-4xl md:text-[2.25rem] md:leading-snug"
          >
            Handpicked homes you can move into soon
          </h2>
          <p className="mt-4 text-lg leading-relaxed text-neutral-600">
            Curated properties our team would confidently recommend to friends and family.
          </p>
        </header>

        {featuredProperties.length > 0 ? (
          <>
            <div
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-8"
              role="list"
              aria-label="Featured property listings"
            >
              {featuredProperties.map((property) => {
                try {
                  const transformedProperty = convertPropertySummaryToDisplay(property);
                  return (
                    <div key={property.id} role="listitem">
                      <PropertyCard property={transformedProperty} />
                    </div>
                  );
                } catch (renderError) {
                  console.error(`💥 FeaturedPropertiesSection: Error rendering property ${property.id}:`, renderError);
                  return (
                    <div
                      key={property.id}
                      className="border p-4 rounded-lg bg-red-50 border-red-200"
                      role="alert"
                      aria-live="polite"
                    >
                      <AlertCircle className="h-6 w-6 text-red-600 mb-2" aria-hidden="true" />
                      <p className="text-sm text-red-500">Property ID: {property.id}</p>
                    </div>
                  );
                }
              })}
            </div>
            <div className="text-center">
              <Link
                to="/properties"
                className="inline-flex items-center rounded-lg bg-primary-600 px-6 py-3 text-sm font-semibold text-white shadow-md shadow-primary-600/25 transition hover:bg-primary-700 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                aria-describedby="view-all-description"
              >
                Browse all homes
              </Link>
              <div id="view-all-description" className="sr-only">
                Browse the complete listing of available properties
              </div>
            </div>
          </>
        ) : (
          <div 
            className="text-center py-12"
            role="status"
            aria-live="polite"
          >
            <div className="max-w-md mx-auto">
              <div className="w-16 h-16 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="h-8 w-8 text-neutral-400" aria-hidden="true" />
              </div>
              <h3 className="text-lg font-semibold text-neutral-800 mb-2">
                No Featured Properties Available
              </h3>
              <p className="text-neutral-600 mb-6">
                No featured properties available at the moment. Check back soon for new listings!
              </p>
              <Link 
                to="/properties" 
                className="inline-flex items-center px-6 py-3 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
              >
                Browse All Properties
              </Link>
            </div>
          </div>
        )}
      </div>
    </section>
  );
});

FeaturedPropertiesSection.displayName = 'FeaturedPropertiesSection';

export default FeaturedPropertiesSection;